import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { FlowLayout, FlowZone, layoutFlow } from './flow-layout';

/** The role of a node, shown as the color of its top stripe. */
export type FlowNodeTone = 'client' | 'managed' | 'private';

/** One label and value inside a node, for a diagram that shows live values. */
export interface FlowNodeRow {
    label: string;
    value: string;
}

export interface FlowNode {
    title: string;
    tone: FlowNodeTone;
    desc?: string;
    rows?: FlowNodeRow[];
}

/** The two-way arrow between two nodes. Links with a zone get a shaded band, with a divider where the zone changes. */
export interface FlowLink {
    label: string;
    sub?: string;
    zone?: FlowZone;
}

let nextId = 0;

/**
 * A "how it works" explainer: a row of nodes with a labeled arrow between each pair.
 * `links[i]` is drawn between `nodes[i]` and `nodes[i + 1]`. The host gives the frame through its own class.
 */
@Component({
    selector: 'app-flow-diagram',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './flow-diagram.component.html',
    styleUrls: ['./flow-diagram.component.scss'],
})
export class FlowDiagramComponent implements OnChanges {
    /** Not named `title`, because a static `title` attribute would also put a native tooltip on the host. */
    @Input() heading = '';
    @Input() nodes: FlowNode[] = [];
    @Input() links: FlowLink[] = [];

    /** Remembers the collapsed state in localStorage under this key. Without a key, the state is not kept. */
    @Input() storageKey = '';

    collapsed = false;

    layout: FlowLayout = layoutFlow(0, []);

    readonly bodyId = `flow-diagram-${nextId++}`;

    /** Selects the stacking breakpoint in the stylesheet. Counts below 2 use the 2-node width, counts above 5 the 5-node width. */
    get sizeClass(): string {
        return `nodes-${Math.min(Math.max(this.nodes.length, 2), 5)}`;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['storageKey']) {
            this.collapsed = this.readCollapsed();
        }
        if (changes['nodes'] || changes['links']) {
            this.layout = layoutFlow(
                this.nodes.length,
                this.links.map((link) => link?.zone)
            );
        }
    }

    toggle(): void {
        this.collapsed = !this.collapsed;
        this.writeCollapsed();
    }

    private fullKey(): string {
        return `flowDiagram.collapsed.${this.storageKey}`;
    }

    private readCollapsed(): boolean {
        if (!this.storageKey) {
            return false;
        }
        try {
            return localStorage.getItem(this.fullKey()) === 'true';
        } catch {
            return false;
        }
    }

    private writeCollapsed(): void {
        if (!this.storageKey) {
            return;
        }
        try {
            localStorage.setItem(this.fullKey(), String(this.collapsed));
        } catch {
            // Blocked storage only costs the remembered state.
        }
    }
}
